import {
  type StagedAccessRequestOf,
  isStagedAccessRequestOf,
} from "@p0security/shared/access/types";
import { AwsAccesses } from "@p0security/shared/integrations/resources/aws/accesses";
import { arnInfo } from "@p0security/shared/integrations/resources/aws/util";
import type { AzureAccesses } from "@p0security/shared/integrations/resources/azure/accesses";
import type { CustomIntegrationAccesses } from "@p0security/shared/integrations/resources/custom/accesses";
import type { GcloudAccesses } from "@p0security/shared/integrations/resources/gcloud/accesses";
import type { GithubAccesses } from "@p0security/shared/integrations/resources/github/accesses";
import type { KubernetesAccesses } from "@p0security/shared/integrations/resources/kubernetes/accesses";
import type { OracleAccesses } from "@p0security/shared/integrations/resources/oracle/accesses";
import type { RdpAccesses } from "@p0security/shared/integrations/resources/rdp/accesses/index";
import type { SelfHostedAccesses } from "@p0security/shared/integrations/resources/self-hosted/accesses";
import type { SnowflakeAccesses } from "@p0security/shared/integrations/resources/snowflake/accesses";
import type { SshAccesses } from "@p0security/shared/integrations/resources/ssh/accesses/index";
import {
  isAwsSshSessionRequest,
  isGcloudSshSessionRequest,
  isKnownProviderSshSessionRequest,
} from "@p0security/shared/integrations/resources/ssh/accesses/index";
import type { ZpaAccesses } from "@p0security/shared/integrations/resources/zpa/accesses";
import { assertNever } from "@p0security/shared/types/index";
import { UNUSED_STAGED_ITEM } from "@p0security/shared/types/lifecycle";
import { StagedPermissionRequest } from "@p0security/shared/types/permission";
import { capitalize, compact } from "lodash";
import pluralize from "pluralize";

type GeneratedDescription =
  | {
      content: string;
      label: string;
      isHidden?: boolean;
    }[]
  | undefined;

export const generatedDescription = (
  request: StagedPermissionRequest
): GeneratedDescription => {
  const { type } = request;

  switch (type) {
    case "aws":
      return isStagedAccessRequestOf<"aws", AwsAccesses>("aws", request)
        ? buildAwsGeneratedDescription(request)
        : undefined;
    case "custom":
      return isStagedAccessRequestOf<"custom", CustomIntegrationAccesses>(
        "custom",
        request
      )
        ? buildCustomGeneratedDescription(request)
        : undefined;
    case "gcloud":
      return isStagedAccessRequestOf<"gcloud", GcloudAccesses>(
        "gcloud",
        request
      )
        ? buildGcloudGeneratedDescription(request)
        : undefined;
    case "snowflake":
      return isStagedAccessRequestOf<"snowflake", SnowflakeAccesses>(
        "snowflake",
        request
      )
        ? buildSnowflakeGeneratedDescription(request)
        : undefined;
    case "k8s":
      return isStagedAccessRequestOf<"k8s", KubernetesAccesses>("k8s", request)
        ? buildK8sGeneratedDescription(request)
        : undefined;
    case "ssh":
      return isStagedAccessRequestOf<"ssh", SshAccesses>("ssh", request)
        ? buildSshGeneratedDescription(request)
        : undefined;
    case "rdp":
      return isStagedAccessRequestOf<"rdp", RdpAccesses>("rdp", request)
        ? buildRdpGeneratedDescription(request)
        : undefined;
    case "azure":
      return isStagedAccessRequestOf<"azure", AzureAccesses>("azure", request)
        ? buildAzureIamGeneratedDescription(request)
        : undefined;
    case "github":
      return isStagedAccessRequestOf<"github", GithubAccesses>(
        "github",
        request
      )
        ? buildGithubGeneratedDescription(request)
        : undefined;
    case "self-hosted":
      return isStagedAccessRequestOf<"self-hosted", SelfHostedAccesses>(
        "self-hosted",
        request
      )
        ? buildSelfHostedGeneratedDescription(request)
        : undefined;
    case "oci":
      return isStagedAccessRequestOf<"oci", OracleAccesses>("oci", request)
        ? buildOracleGeneratedDescription(request)
        : undefined;
    case "zscaler-private-access":
      return isStagedAccessRequestOf<"zscaler-private-access", ZpaAccesses>(
        "zscaler-private-access",
        request
      )
        ? buildZpaGeneratedDescription(request)
        : undefined;
    // Resources without generated content
    case "tailscale":
    case "aws-rds":
    case "azure-ad":
    case "entra-id":
    case "mysql":
    case "pg":
    case "okta":
    case "splunk-stack":
    case "workspace":
      return undefined;
    default:
      console.warn("Got unexpected permission request", {
        type,
      });
      return undefined;
  }
};

const buildOracleGeneratedDescription = (
  request: StagedAccessRequestOf<OracleAccesses>
) => {
  return [
    {
      label: "User OCID",
      content: request.generated.userId,
    },
    {
      label: "Domain OCID",
      content: request.permission.domainId,
    },
    {
      label: "Compartment OCID",
      content: request.permission.compartmentId,
    },
    {
      label: "Group OCID",
      content: request.permission.groupId,
    },
  ];
};

const buildAzureIamGeneratedDescription = (
  request: StagedAccessRequestOf<AzureAccesses>
) => [
  {
    label: `Role Assignment ID`,
    content: request.permission.roleAssignmentId,
  },
];

const buildSelfHostedGeneratedDescription = (
  request: StagedAccessRequestOf<SelfHostedAccesses>
) => [
  {
    label: "Linux Username",
    content: request.permission.linuxUserName,
  },
  {
    label: "Host Name",
    content: request.permission.hostname,
  },
  {
    label: "Host ID",
    content: request.permission.hostId,
  },
  {
    label: "Public IP",
    content: request.permission.publicIp,
  },
  {
    label: "Fingerprint",
    content: request.permission.fingerprint,
  },
];

/**
 * Returns a list of generated access properties. These properties may be included in the
 * parameter or generated sections of the request.
 */
const buildAwsGeneratedDescription = (
  request: StagedAccessRequestOf<AwsAccesses>
): GeneratedDescription => {
  switch (request.access) {
    case "policy": {
      return [
        ...buildAwsPolicyGenerated(request.generated),
        ...buildAwsPolicyPermission(request.permission),
      ];
    }
    case "resource": {
      return [
        ...buildAwsResourceGenerated(request.generated),
        ...buildAwsResourcePermission(request.permission),
      ];
    }
    default: // No other AWS requests have generated content
      return undefined;
  }
};

const buildCustomGeneratedDescription = (
  request: StagedAccessRequestOf<CustomIntegrationAccesses>
) => {
  switch (request.access) {
    case "resource": {
      try {
        // If the ARN is invalid, we do not display the generated content
        return [
          {
            label: `${request.permission.application}`,
            content: request.permission.item,
          },
        ];
      } catch (err) {
        return undefined;
      }
    }
    default: // No other AWS requests have generated content
      return undefined;
  }
};

const buildSnowflakeGeneratedDescription = (
  request: StagedAccessRequestOf<SnowflakeAccesses>
) => {
  if (request.access === "sql") {
    return [
      { label: "Role", content: request.generated.role },
      ...(request.permission.neededRoles
        ? [
            {
              label: "Additional granted roles",
              content: request.permission.neededRoles.join(", "),
            },
          ]
        : []),
    ];
  }

  return undefined;
};

const buildK8sGeneratedDescription = ({
  delegation,
  generated,
}: StagedAccessRequestOf<KubernetesAccesses>) => {
  return [
    { label: "Kubernetes Role", content: generated.role },
    ...(delegation?.aws?.generated
      ? [
          {
            label: "AWS permission-set role",
            content: delegation.aws.generated.arn,
          },
          {
            label: "AWS Policy",
            content: delegation.aws.generated.policyName,
          },
        ]
      : []),
    ...(delegation?.aws
      ? [
          {
            label: "AWS role",
            content: delegation.aws.permission.name,
          },
        ]
      : []),
  ];
};

const buildGcloudGeneratedDescription = (
  request: StagedAccessRequestOf<GcloudAccesses>
) => {
  switch (request.access) {
    case "role":
      return;
    case "resource":
      return buildGcloudResourceGenerated(request.generated);
    case "permission":
      return [
        {
          label: "Role",
          content: request.generated.role,
        },
      ];
    default:
      throw assertNever(request);
  }
};

const buildGcloudResourceGenerated = (
  generated: StagedAccessRequestOf<GcloudAccesses, "resource">["generated"]
) => [
  {
    label: pluralize("Role", generated.roles.length),
    content: generated.roles.map((r) => r.role).join(", "),
  },
];

const buildAwsPolicyPermission = (
  permission: StagedAccessRequestOf<AwsAccesses, "policy">["permission"]
) => {
  try {
    return compact([
      permission.extendsName && {
        label: `Extends Policy`,
        content: permission.extendsName,
      },
    ]);
  } catch (error) {
    return [];
  }
};

const buildAwsPolicyGenerated = (
  generated: StagedAccessRequestOf<AwsAccesses, "policy">["generated"]
) => {
  try {
    // IDC policies do not have a generated ARN but include a name.
    if (
      generated.arn === UNUSED_STAGED_ITEM &&
      generated.name !== UNUSED_STAGED_ITEM
    ) {
      return [
        {
          label: "Policy Name",
          content: generated.name,
        },
      ];
    }

    // If the ARN is invalid, we do not display the generated content
    const info = arnInfo(generated.arn);
    return [
      {
        label: `${capitalize(info.resourceType)}`,
        content: generated.name,
      },
    ];
  } catch (err) {
    return [];
  }
};

const buildAwsResourcePermission = (
  permission: StagedAccessRequestOf<AwsAccesses, "resource">["permission"]
) => {
  try {
    return compact([
      permission.extendsName && {
        label: `Extends Policy`,
        content: permission.extendsName,
      },
    ]);
  } catch (error) {
    return [];
  }
};

const buildAwsResourceGenerated = (
  generated: StagedAccessRequestOf<AwsAccesses, "resource">["generated"]
) => {
  try {
    const resourceArn = getArnGeneratedContent(generated.arn, generated.name);
    const policyArn = getArnGeneratedContent(
      generated.policyArn,
      generated.policyName
    );
    return compact([resourceArn, policyArn]);
  } catch (err) {
    return [];
  }
};

const getArnGeneratedContent = (arn: string, name: string) => {
  try {
    const info = arnInfo(arn);
    return {
      label: `${capitalize(info.resourceType)}`,
      content: name,
    };
  } catch (err) {
    // If the ARN is invalid, we do not display the generated content
    return;
  }
};

const buildSshGeneratedDescription = (
  request: StagedAccessRequestOf<SshAccesses>
) => {
  if (
    request.access === "all" ||
    request.access === "group" ||
    request.access === "parent"
  )
    return;

  if (!isKnownProviderSshSessionRequest(request)) return;

  if (isGcloudSshSessionRequest(request)) {
    return buildGcloudResourceGenerated(request.generated);
  }
  if (isAwsSshSessionRequest(request)) {
    const generated = request.generated;

    const resources = buildAwsResourceGenerated(generated.resource) ?? [];
    const linuxUsernameEntry = {
      label: `Linux Username`,
      content: generated.linuxUserName,
    };
    const publicKeyEntry = generated.publicKey
      ? [
          {
            label: "Public Key",
            content: generated.publicKey,
            isHidden: true,
          },
        ]
      : [];

    return [...resources, linuxUsernameEntry, ...publicKeyEntry];
  }

  return;
};

const buildRdpGeneratedDescription = (
  request: StagedAccessRequestOf<RdpAccesses>
) => {
  if (request.access !== "session") return;

  const { permission } = request;
  const provider = permission.provider ?? "entra";

  const publicKeyEntry =
    provider === "proxy" && request.generated?.publicKey
      ? [
          {
            label: "Public Key",
            content: request.generated.publicKey,
            isHidden: true,
          },
        ]
      : [];

  return compact([
    provider === "proxy" && permission.user
      ? { label: "User", content: permission.user }
      : undefined,
    ...publicKeyEntry,
  ]);
};

const buildGithubGeneratedDescription = (
  request: StagedAccessRequestOf<GithubAccesses>
) => {
  return [
    {
      label: "Username",
      content: request.permission.username,
    },
  ];
};

const buildZpaGeneratedDescription = (
  request: StagedAccessRequestOf<ZpaAccesses>
) => [
  { label: "Access Policy Rule Name", content: request.generated.ruleName },
];
